import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { request } from "./shared";
import { StateBlock, Skeleton } from "../../components/UI";
import MatchRow from "../../components/UI/MatchRow";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { matchState, matchTitle } from "../../components/UI/matchLabels";
import { matchOutcome, StatsMatch } from "../Stats/statsUtils";

const Section = styled.section`
  display: grid;
  gap: 8px;
  min-width: 0;
  > header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 0 8px;
    min-height: 36px;
    h2 {
      font-size: 18px;
      line-height: 26px;
      font-weight: 600;
    }
    a {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      font-size: 14px;
      font-weight: 600;
      color: ${(p) => p.theme.colors.textSecondary};
    }
    a:hover {
      color: ${(p) => p.theme.colors.text};
      text-decoration: underline;
    }
  }
  ul {
    list-style: none;
    display: grid;
    gap: 2px;
  }
`;

const LIMIT = 5;

/** Пять последних матчей без отдельной карточки на строку; остальные — на странице статистики. */
export default function History() {
  const { t } = useLanguage();
  const f = useFormat();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    request("/stats/me/matches?page=1&pageSize=" + LIMIT)
      .then((d) => alive && setItems((d.items || []).slice(0, LIMIT)))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [nonce]);

  return (
    <Section aria-labelledby="recent-title">
      <header>
        <h2 id="recent-title">{t("home.recent")}</h2>
        <Link to="/stats">{t("home.all_matches")}</Link>
      </header>
      {loading && items.length === 0 ? (
        <div role="status" aria-label={t("common.loading")} style={{ display: "grid", gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} $h={68} $r={12} />
          ))}
        </div>
      ) : error && items.length === 0 ? (
        <StateBlock kind="error" title={t("home.history_error")} text={error} onAction={() => setNonce((n) => n + 1)} />
      ) : items.length === 0 ? (
        <StateBlock kind="empty" title={t("home.history_empty")} text={t("home.history_empty_text")} />
      ) : (
        <ul>
          {items.map((m) => {
            const outcome = matchOutcome({
              status: m.status,
              score: [m.score_ct ?? 0, m.score_t ?? 0],
              team: m.team,
            } as unknown as StatsMatch);
            const own: [number, number] = m.team === "T" ? [m.score_t ?? 0, m.score_ct ?? 0] : [m.score_ct ?? 0, m.score_t ?? 0];
            return (
              <MatchRow
                key={m.id}
                to={"/match/" + m.id}
                map={m.map || null}
                title={matchTitle(t, m.map, m.status)}
                date={f.date(m.created_at)}
                outcome={m.status === "cancelled" ? "cancelled" : outcome}
                stateLabel={matchState(t, m.status, outcome)}
                score={outcome === "pending" ? null : own}
              />
            );
          })}
        </ul>
      )}
    </Section>
  );
}
